import { extend, findIndex, rgbToHex, compare } from "@dhx/ts-common/core";
import { IGrid, Grid } from "@dhx/ts-grid";
import { DataPage, str2pos } from "@xbs/muon";
import {
	ICell,
	ICellMeta,
	Id,
	ICopyData,
	IRange,
	IRangeIndex,
	IActionConfig,
	IExecuteConfig,
	ISpan,
} from "./../types";
import { alert } from "@dhx/ts-message";
import locale from "../locales/en";
declare const dhx: any;

export function getLetterFromNumber(num: number): string {
	if (!num) {
		return "";
	}
	num = --num;
	const numeric = num % 26;
	const letter = String.fromCharCode(65 + numeric);
	const group = Math.floor(num / 26);
	if (group > 0) {
		return getLetterFromNumber(group) + letter;
	}
	return letter;
}

export function getNumberFromLetter(str: string): number {
	if (!str) {
		return -1;
	}
	str = str.toUpperCase();
	const letters = str.split("");
	return letters.reduce((total, item, i, arr) => {
		const num = item.charCodeAt(0) - 64;
		const pow = Math.pow(26, arr.length - (i + 1));
		total += num * pow;
		return total;
	}, 0);
}

export function getCellIds(cell: string, grid: IGrid): ICell | IRange | any {
	if (!cell) {
		return {};
	}
	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	const match = cell.match(/([a-zA-Z]*\d*):([a-zA-Z]*\d*)/);

	if (match) {
		const start = getCellIds(match[1], grid);
		const end = getCellIds(match[2], grid);
		const startRow = +start.row <= end.row ? start.row : end.row;
		const startCol = +start.col <= end.col ? start.col : end.col;
		return {
			start: { row: startRow, col: startCol },
			end: {
				row: startRow === end.row ? start.row : end.row,
				col: startCol === end.col ? start.col : end.col,
			},
		};
	}

	// eslint-disable-next-line @typescript-eslint/no-use-before-define
	const index = getCellIndex(cell, grid);
	if (!index) {
		return {};
	}

	const [row, col] = str2pos(cell);
	return {
		col,
		row,
	};
}
function findBinaryIndex(arr, x, start, end) {
	// Base Condition
	if (start > end) return -1;

	// Find the middle index
	let mid = Math.floor((start + end) / 2);
	if (!arr[mid]) return mid;
	const id = +arr[mid].id;
	// Compare mid with given key x
	if (id === x) return mid;

	// If element at mid is greater than x,
	// search in the left half of mid
	if (id > x) return findBinaryIndex(arr, x, start, mid - 1);
	// If element at mid is smaller than x,
	// search in the right half of mid
	else return findBinaryIndex(arr, x, mid + 1, end);
}

export function getCellIndex(cell: string, grid: IGrid, includeHidden = false): { row: number; col: number } {
	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	const match = cell.match(/([a-zA-Z]*)(\d*)/);
	if (!match || !match[1] || !match[2]) {
		return;
	}
	const r = parseInt(match[2], 10);
	const arr = (grid.data as any)[includeHidden ? "_initOrder" : "_order"];

	return {
		col: getNumberFromLetter(match[1]),
		row: findBinaryIndex(arr, r, 0, r),
	};
}

export function getCellNameByIndex(rowIndex: number, colIndex: number, grid: IGrid): string {
	const row = grid.data.getId(rowIndex) || rowIndex + 1;
	return `${getLetterFromNumber(colIndex)}${row}`;
}

export function getCellNameById(grid: IGrid, row: Id, col: Id): string {
	if (!row) {
		return "";
	}

	const rowId = grid.data.getItem(row as any)?.$index || row;
	let colId = findIndex(grid.config.columns, item => col.toString() === item.id);
	if (colId < 0 && col > 0) {
		colId = +col;
	}

	if (rowId < 0 || colId < 0) {
		return;
	}
	return `${getLetterFromNumber(colId)}${rowId}`;
}

export function getCellInfo(cell: string, page: DataPage, grid: IGrid): ICellMeta {
	let cellId = getCellIds(cell, grid);
	if (cellId.start) {
		cellId = cellId.start;
	}

	if (!cellId || !cell) {
		return {
			locked: false,
		};
	}

	return page.getCell(cellId.row, cellId.col) || {};
}

export function isRangeId(id: string): boolean {
	return new RegExp(/([A-Z]+\d+:[A-Z]+\d+)|(?:,)([A-Z]+\d+)/).test(id);
}

export function getRangeIndexes(range: string, grid: IGrid, includeHidden = false): IRangeIndex {
	let cells;
	if (range.includes(":")) {
		cells = range.split(":").map(item => getCellIndex(item, grid, includeHidden));
	} else {
		cells = range.split(",").map(item => getCellIndex(item, grid, includeHidden));
	}
	const res = {
		start: {
			row: Math.min(cells[0].row, cells[1].row),
			col: Math.min(cells[0].col, cells[1].col),
		},
		end: {
			row: Math.max(cells[0].row, cells[1].row),
			col: Math.max(cells[0].col, cells[1].col),
		},
	};

	return res;
}

type rangeDir = "row" | "col";

export function getRangeArray(range: string, grid: IGrid, dir: rangeDir = "row"): string[] {
	const arr = [];
	const opp = dir === "row" ? "col" : "row";
	if (range) {
		const { start, end } = getRangeIndexes(range, grid, true);
		for (let i = start[dir]; i <= end[dir]; i++) {
			for (let k = start[opp]; k <= end[opp]; k++) {
				const cell =
					dir === "row" ? getCellNameById(grid, i + 1, k) : getCellNameById(grid, k + 1, i);
				arr.push(cell);
			}
		}
	}
	return arr;
}

export function getRangeMatrix(range: string, grid: IGrid, dir: rangeDir = "row") {
	const arr = [];
	const opp = dir === "row" ? "col" : "row";
	if (range) {
		const { start, end } = getRangeIndexes(range, grid);
		for (let i = start[dir]; i <= end[dir]; i++) {
			const f = [];
			for (let k = start[opp]; k <= end[opp]; k++) {
				const cell = dir === "row" ? getCellNameByIndex(i, k, grid) : getCellNameByIndex(k, i, grid);
				f.push(cell);
			}
			arr.push(f);
		}
	}
	return arr;
}

export function getNextRangeCell(range: string, current: string, grid: IGrid, dir: rangeDir = "row"): string {
	const rangeArr = getRangeArray(range, grid, dir);
	return rangeArr[rangeArr.indexOf(current) + 1] || rangeArr[0];
}

export function getPrevRangeCell(range: string, current: string, grid: IGrid, dir: rangeDir = "row"): string {
	const rangeArr = getRangeArray(range, grid, dir);
	return rangeArr[rangeArr.indexOf(current) - 1] || rangeArr[rangeArr.length - 1];
}

export function getCellsArray(cells: string, grid: IGrid): string[] {
	if (!cells) {
		return;
	}
	const arr = cells.split(",");
	return arr.reduce((total, cell) => {
		cell = cell.toUpperCase();
		if (isRangeId(cell)) {
			total = total.concat(getRangeArray(cell, grid));
		} else {
			total.push(cell);
		}
		return total;
	}, []);
}

export function extendConfig(target = {}, source = {}, deep = true) {
	for (const key in target) {
		const sobj = source[key];
		const tobj = target[key];

		if (tobj && "validate" in tobj) {
			source[key] = tobj.validate(sobj) ? sobj : tobj.default;
			target[key] = tobj.default;
		}
	}

	return extend(target, source, deep);
}

export function cleanActionConfig(config: IExecuteConfig | IActionConfig): any {
	const {
		page,
		dataStore,
		sheets,
		editLine,
		grid,
		activePage,
		spreadsheet,
		mouseEvent,
		events,
		...actionConfig
	} = config;
	return actionConfig;
}

declare const WebAssembly: any;

export function isWasmSupported(): boolean {
	try {
		if (typeof WebAssembly === "object" && typeof WebAssembly.instantiate === "function") {
			const module = new WebAssembly.Module(
				Uint8Array.of(0x0, 0x61, 0x73, 0x6d, 0x01, 0x00, 0x00, 0x00)
			);
			if (module instanceof WebAssembly.Module) {
				return new WebAssembly.Instance(module) instanceof WebAssembly.Instance;
			}
		}
	} catch (e) {
		return false;
	}
	return false;
}

export function fetchFile(
	url: string,
	method?: "GET",
	responseType?: XMLHttpRequestResponseType
): Promise<any> {
	return new Promise((res, rej) => {
		const req = new XMLHttpRequest();
		req.open(method, url, true);
		req.responseType = responseType;
		req.onload = () => {
			const response = req.response;
			if (response) {
				res(response);
			} else {
				rej(req);
			}
		};

		req.onerror = () => rej(req);

		req.send(null);
	});
}

export function parseHtmlTable(html: string): ICopyData {
	const styleString = html.substring(html.indexOf("<style"), html.indexOf("</style>"));
	const css = {};
	if (styleString.includes(".x")) {
		const classesString = styleString.substring(styleString.indexOf(".x"), styleString.indexOf("-->"));
		const classes = classesString.split("\r\n.");
		classes[0] = classes[0].substr(1);
		for (const item of classes) {
			if (!item) continue;
			const name = item.substring(0, item.indexOf("\r\n\t"));
			css[name] = stylesFromString(item.substring(item.indexOf("{") + 1, item.indexOf("}")).trim());
		}
	}
	const tableString = html.substring(html.indexOf("<table"), html.indexOf("</table>") + 8);
	const container = document.createElement("div");
	container.innerHTML = tableString;
	const table = container.querySelector("table");
	const rowsCount = table.rows.length;
	const colsCount = table.rows[0].cells.length;
	const values = [];
	const styles = [];
	for (let i = 0; i < table.rows.length; i++) {
		const row = table.rows[i];
		for (let d = 0; d < row.cells.length; d++) {
			const col = row.cells[d];
			values.push(col.innerText.trim());
			let style = {};
			for (let s = 0; s < col.classList.length; s++) {
				const classItem = col.classList[s];
				if (css[classItem]) {
					style = {
						...style,
						...css[classItem],
					};
				}
			}
			style = { ...style, ...stylesFromString(col.getAttribute("style")) };
			styles.push(style);
		}
	}
	return { values, styles, rowsCount, colsCount };
}

export function parseFromText(str): ICopyData {
	const parsed = str.split(/\n/);
	let values = [];
	for (const item of parsed) {
		const newItems = item.split(/\t/);
		values = values.concat(newItems);
	}
	const rowsCount = parsed.length;
	const colsCount = parsed[0].split("\t").length;
	return { values, rowsCount, colsCount };
}

function stylesFromString(str) {
	if (!str) return;
	const styles = str.split(";");
	const objStyles = {};
	for (const style of styles) {
		if (!style) continue;
		const keys = style.trim().split(":");
		if (keys[0] === "height" || keys[0] === "width") continue;
		objStyles[keys[0]] = keys[1];
	}
	return objStyles;
}

export function getTextHeight(text: any, width: number) {
	const el = document.createElement("div");
	el.style.width = `${width}px`;
	el.innerText = text;
	el.classList.add("dhx-spreadsheet-height-detector");
	document.body.appendChild(el);
	const height = el.scrollHeight;
	el.remove();
	return height;
}

export function convertStyles(styles: any): any {
	if (!styles) return {};
	let justify = null;
	let space = "nowrap";
	let wordBreak = "normal";
	let align;
	const css = { ...styles };
	const textAlign = css.textAlign || css["text-align"];
	const verticalAlign = css.verticalAlign || css["vertical-align"];
	if (textAlign) {
		switch (textAlign) {
			case "left":
				justify = "flex-start";
				break;
			case "center":
				justify = "center";
				break;
			case "right":
				justify = "flex-end";
				break;
		}
		css["justify-content"] = justify;
	}
	if (verticalAlign) {
		switch (verticalAlign) {
			case "top":
				align = "flex-start";
				break;
			case "center":
				align = "center";
				break;
			case "bottom":
				align = "flex-end";
				break;
		}
		css["align-items"] = `${align} !important`;
	}
	const { multiline, color, background, "background-color": bc } = styles;
	if (multiline) {
		space = multiline === "wrap" ? "normal" : "nowrap";
		wordBreak = multiline === "wrap" ? "break-all" : "normal";
		css["white-space"] = space;
		css["word-break"] = wordBreak;
	}
	if (color) {
		css["color"] = rgbToHex(color);
	}
	if (bc) {
		css["background-color"] = rgbToHex(bc);
	}
	if (background) {
		css["background"] = rgbToHex(background);
		css["background-color"] = rgbToHex(background);
	}

	return css;
}

export function isMac() {
	// eslint-disable-next-line @typescript-eslint/prefer-regexp-exec
	return !!(navigator as any).userAgentData?.platform.match(/(Mac)/i);
}
export function getCellNode(cell: string, grid: Grid, includeHidden = false): HTMLElement {
	const { row, col } = getCellIndex(cell, grid, includeHidden);
	const gridNode = grid.getRootNode();
	const rowNode = gridNode.querySelector(`.dhx_grid_data [data-dhx-id='${row + 1}']`);
	if (!rowNode) return;
	return rowNode.querySelector(`[data-dhx-col-id='${col}']`);
}

export function isURL(text: any): string | boolean {
	if (typeof text !== "string" || text.includes(" ")) return false;
	try {
		new URL(text);
		return text;
	} catch {
		return false;
	}
}

export function doesContainUrl(value: any): string | undefined {
	if (typeof value !== "string") return;

	return value.split(" ").find(text => isURL(text));
}

export function getStylesObject(claasName: string = "") {
	let cssClasses = claasName || "";
	const css = cssClasses.split(" ") || [];
	let styles = {};
	css.forEach(className => {
		styles = { ...styles, ...dhx.css.get(className) };
	});
	return styles;
}
export function checkSpan({ from, to }: ISpan, leftSplit, topSplit, grid) {
	const { column: fColumn, row: fRow } = from;
	const { column: tColumn, row: tRow } = to;
	const firstCell = getCellNameByIndex(fRow, fColumn + 1, grid);
	const lastCell = getCellNameByIndex(tRow, tColumn + 1, grid);
	return checkSpanByNames(`${firstCell}:${lastCell}`, leftSplit, topSplit, grid);
}

export function checkSpanByNames(cell: string, leftSplit, topSplit, grid) {
	const [firstCell, lastCell] = cell.split(":");
	return compare(
		isCellFixed(firstCell, leftSplit, topSplit, grid),
		isCellFixed(lastCell, leftSplit, topSplit, grid)
	);
}

export function isCellFixed(
	cell: string,
	leftSplit,
	topSplit,
	grid
): { isTopFixed: boolean; isLeftFixed: boolean } {
	const { col, row } = getCellIds(cell, grid);
	const ls = leftSplit ?? -1;
	const ts = topSplit ?? -1;
	const isLeftFixed = ls > col;
	const isTopFixed = ts > row;
	return { isTopFixed, isLeftFixed };
}

export function isCellSpaned(cell: string, merged: ISpan[], grid): ISpan | undefined {
	if (!merged?.length) return;
	const { col, row } = getCellIds(cell, grid);
	return merged.find(({ from, to }) => {
		const { row: fRow, column: fCol } = from;
		const { row: tRow, column: tCol } = to;
		return fRow <= row && tRow >= row && fCol <= col && tCol >= col;
	});
}

export function showAlert(text: string) {
	alert({
		header: locale.alertTitle,
		text,
		buttons: [locale.ok],
		buttonsAlignment: "right",
	});
}
